import axios, { AxiosInstance } from 'axios';

// Pull from environment variable injected at build time
const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api/SupplierTool';

const getApiErrorMessage = (error: any, fallback: string) => {
  const data = error?.response?.data;
  if (typeof data === 'string' && data.trim()) return data;
  return data?.message || data?.Message || fallback;
};

// Create axios instance with interceptor for authentication
let axiosInstance: AxiosInstance | null = null;
let tokenGetter: ((forceRefresh?: boolean) => Promise<string | null>) | null = null;

export const initializeAxios = (getAccessToken: (forceRefresh?: boolean) => Promise<string | null>) => {
  // Prevent re-initialization if already initialized
  if (axiosInstance && tokenGetter) {
    console.log('Axios already initialized, updating token getter only');
    tokenGetter = getAccessToken;
    return;
  }
  
  tokenGetter = getAccessToken;
  axiosInstance = axios.create({
    baseURL: BASE_URL,
  });
  
  console.log('Axios instance created with baseURL:', BASE_URL);

  // Add request interceptor to include access token
  axiosInstance.interceptors.request.use(
    async (config) => {
      if (tokenGetter) {
        try {
          const token = await tokenGetter();
          if (token) {
            // Decode token to inspect audience (for debugging)
            try {
              const tokenParts = token.split('.');
              if (tokenParts.length === 3) {
                const payload = JSON.parse(atob(tokenParts[1].replace(/-/g, '+').replace(/_/g, '/')));
                console.log('Token details - Audience:', payload.aud, 'Issuer:', payload.iss, 'Expires:', new Date(payload.exp * 1000));
              }
            } catch (e) {
              // Ignore decoding errors
            }
            
            // Set Authorization header
            config.headers = config.headers || {};
            config.headers['Authorization'] = `Bearer ${token}`;
            console.log('Token attached to request:', config.url, 'Token length:', token.length);
          } else {
            console.warn('Token getter returned null for request:', config.url);
          }
        } catch (error) {
          console.error('Error getting token in interceptor:', error);
        }
      } else {
        console.warn('Token getter not available for request:', config.url);
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Add response interceptor to handle 401 errors
  // Track retry attempts to prevent infinite loops
  const retryCounts = new Map<string, number>();
  const MAX_RETRIES = 1; // Only retry once per request
  
  axiosInstance.interceptors.response.use(
    (response) => {
      // Clear retry count on success
      if (response.config.url) {
        retryCounts.delete(response.config.url);
      }
      return response;
    },
    async (error) => {
      const requestUrl = error.config?.url;
      
      if (error.response?.status === 401 && tokenGetter && requestUrl) {
        const retryCount = retryCounts.get(requestUrl) || 0;
        const authErrorDesc = (error.response?.headers?.['x-auth-error-description'] || '') as string;
        const isSignatureKeyError = /signature key|key was not found|SecurityTokenSignatureKey/i.test(authErrorDesc);
        
        // Only retry if we haven't exceeded max retries
        if (retryCount < MAX_RETRIES) {
          retryCounts.set(requestUrl, retryCount + 1);
          
          // Force refresh when backend says signing key not found (stale token after Azure AD key rotation)
          const forceRefresh = isSignatureKeyError;
          if (forceRefresh) {
            console.warn('API returned "signature key not found" - forcing fresh token from Azure AD');
          }
          const token = await tokenGetter(forceRefresh);
          if (token && error.config) {
            error.config.headers.Authorization = `Bearer ${token}`;
            return axiosInstance!.request(error.config);
          }
        } else {
          // Max retries reached, log detailed error information from API
          console.error(`Max retries (${MAX_RETRIES}) reached for ${requestUrl}. Authentication failed.`);
          
          // Log detailed error information from API response headers
          if (error.response?.headers) {
            const authError = error.response.headers['x-auth-error'] || error.response.headers['x-auth-failed-error'];
            const authErrorCode = error.response.headers['x-auth-error-code'] || error.response.headers['x-auth-failed-exception'];
            const authErrorMessage = error.response.headers['x-auth-error-description'] || error.response.headers['x-auth-failed-message'];
            
            console.group('🔴 API Authentication Error Details');
            console.error('Request URL:', requestUrl);
            console.error('Status:', error.response.status);
            
            if (authErrorCode) {
              console.error('Error Code/Exception:', authErrorCode);
            }
            if (authErrorMessage) {
              console.error('Error Message:', authErrorMessage);
            }
            
            // Parse error details from headers (JSON string in X-Auth-Error or X-Auth-Failed-Error header)
            if (authError) {
              try {
                const errorData = typeof authError === 'string' ? JSON.parse(authError) : authError;
                console.group('📋 Detailed Error Information');
                Object.entries(errorData).forEach(([key, value]) => {
                  console.log(`${key}:`, value);
                });
                console.groupEnd();
              } catch (e) {
                // If parsing fails, just show the raw string
                console.error('Error Details (Raw):', authError);
              }
            }
            
            // Also log the full response headers for debugging
            console.log('Response Headers:', error.response.headers);
            console.log('Response Data:', error.response.data);
            console.groupEnd();
          } else {
            console.error('No error details in response headers. Full error:', error);
          }
          
          retryCounts.delete(requestUrl);
        }
      } else if (error.response?.status === 401) {
        // Log 401 errors even if not retrying
        console.group('🔴 API Authentication Error (401 Unauthorized)');
        console.error('Request URL:', requestUrl || error.config?.url);
        console.error('Status:', error.response.status);
        
        if (error.response?.headers) {
          const authError = error.response.headers['x-auth-error'] || error.response.headers['x-auth-failed-error'];
          const authErrorCode = error.response.headers['x-auth-error-code'] || error.response.headers['x-auth-failed-exception'];
          const authErrorMessage = error.response.headers['x-auth-error-description'] || error.response.headers['x-auth-failed-message'];
          
          if (authError) {
            try {
              const errorData = typeof authError === 'string' ? JSON.parse(authError) : authError;
              console.error('Error Details (Parsed):', errorData);
            } catch {
              console.error('Error Details:', authError);
            }
          }
          if (authErrorCode) {
            console.error('Error Code/Exception:', authErrorCode);
          }
          if (authErrorMessage) {
            console.error('Error Message:', authErrorMessage);
          }
          
          // Parse error details from headers (JSON string in X-Auth-Error or X-Auth-Failed-Error header)
          if (authError) {
            try {
              const errorData = typeof authError === 'string' ? JSON.parse(authError) : authError;
              console.group('📋 Detailed Error Information');
              Object.entries(errorData).forEach(([key, value]) => {
                console.log(`${key}:`, value);
              });
              console.groupEnd();
            } catch (e) {
              // If parsing fails, just show the raw string
              console.log('Error Details (Raw):', authError);
            }
          }
          
          console.log('Response Headers:', error.response.headers);
        }
        
        console.log('Response Data:', error.response.data);
        console.groupEnd();
      }
      
      return Promise.reject(error);
    }
  );
};

// Helper function to check if axios is initialized
export const isAxiosInitialized = () => {
  return axiosInstance !== null && tokenGetter !== null;
};

// Helper function to get axios instance
const getAxiosInstance = () => {
  if (!axiosInstance) {
    // Don't use default axios - it won't have authentication
    console.error('Axios instance not initialized. API calls will fail without authentication.');
    // Return a mock axios instance that will fail with a clear error
    return {
      get: () => Promise.reject(new Error('Axios not initialized - authentication required')),
      post: () => Promise.reject(new Error('Axios not initialized - authentication required')),
      put: () => Promise.reject(new Error('Axios not initialized - authentication required')),
      delete: () => Promise.reject(new Error('Axios not initialized - authentication required')),
    } as any;
  }
  return axiosInstance;
};

// Fetch Commodity Managers from API
export const fetchCommodityManagers = async () => {
  try {
    const response = await getAxiosInstance().get(`${BASE_URL}/CommodityManager`); // Update endpoint if needed
    return response.data;
  } catch (error) {
    console.error('Error fetching Commodity Managers:', error);
    return [];
  }
};

export type AuthorizedUserResult = {
  authorized: boolean;
  email?: string | null;
  message?: string;
};

/** Checks whether the signed-in user is in the Commodity Managers list. */
export const checkAuthorizedUser = async (): Promise<AuthorizedUserResult> => {
  try {
    const response = await getAxiosInstance().get(`${BASE_URL}/IsAuthorizedUser`);
    const data = response.data ?? {};
    return {
      authorized: Boolean(data.authorized),
      email: data.email ?? null,
      message: data.message ?? undefined,
    };
  } catch (error: any) {
    console.error('Error checking authorized user:', error);
    return {
      authorized: false,
      message: error?.response?.data?.message || 'Unable to verify access. Please try again.',
    };
  }
};

export const fetchAllSuppliersbyCommodityId = async (commodityId: number) => {
  try {
    const response = await getAxiosInstance().get(`${BASE_URL}/GetAllSuppliers?commodityId=${commodityId}`);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching All Suppliers:', error);
    // Don't retry on 401 - it's an authentication issue
    if (error?.response?.status === 401) {
      console.error('Authentication failed. Token may be invalid or expired.');
      throw error; // Re-throw to let caller handle authentication errors
    }
    return [];
  }
};

export const fetchAllCommodities = async () => {
    try {
        const response = await getAxiosInstance().get(`${BASE_URL}/Commodity`);
        return response.data;
    } catch (error: any) {
        console.error('Error fetching All Commodities:', error);
        // Don't retry on 401 - it's an authentication issue
        if (error?.response?.status === 401) {
            console.error('Authentication failed. Token may be invalid or expired.');
            throw error; // Re-throw to let caller handle authentication errors
        }
        return [];
    }
};

export const fetchAllSuppliers = async () => {
  try {
    const response = await getAxiosInstance().get(`${BASE_URL}/GetAllSuppliers`);
    return response.data;
  } catch (error: any) {
    console.error('Error fetching All Suppliers:', error);
    // Don't retry on 401 - it's an authentication issue
    if (error?.response?.status === 401) {
      console.error('Authentication failed. Token may be invalid or expired.');
      throw error; // Re-throw to let caller handle authentication errors
    }
    return [];
  }
};

export const addSupplier = async (supplierData: any) => {
  try {
    const response = await getAxiosInstance().post(`${BASE_URL}/NewSupplier`, supplierData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const UpdateSupplier = async (supplierData: any) => {
  try {
    const response = await getAxiosInstance().post(`${BASE_URL}/UpdateSupplier`, supplierData);
    return response.data;
  } catch (error: any) {
    const message = error?.response?.data;
    if (typeof message === 'string' && message.trim()) {
      throw new Error(message);
    }
    throw error;
  }
};

// Add new Commodity Manager to API
export const addCommodityManager = async (mail: string, isSuperUser = false) => {
  try {
    const response = await getAxiosInstance().post(`${BASE_URL}/CreateCommodityManager`, {
      mail,
      isSuperUser,
    });
    return response.data;
  } catch (error: any) {
    console.error('Error adding Commodity Manager:', error);
    throw new Error(getApiErrorMessage(error, 'Error adding Commodity Manager'));
  }
};

export const updateCommodityManagerAdmin = async (mail: string, isSuperUser: boolean) => {
  try {
    const response = await getAxiosInstance().post(`${BASE_URL}/UpdateCommodityManagerAdmin`, {
      mail,
      isSuperUser,
    });
    return response.data;
  } catch (error: any) {
    console.error('Error updating Commodity Manager Admin:', error);
    throw new Error(getApiErrorMessage(error, 'Error updating Admin access'));
  }
};

export const deleteCommodityManager = async (mail: string) => {
  try {
    const response = await getAxiosInstance().delete(`${BASE_URL}/DeleteCommodityManager?email=${mail}`);
    return response.data;
  } catch (error: any) {
    console.error('Error deleting Commodity Manager:', error);
    throw new Error(getApiErrorMessage(error, 'Error deleting Commodity Manager'));
  }
};

export type ParseDataSupplier = {
  supplierIFA: string;
  supplierName: string;
};

export type ParseDataFinancialDetail = {
  fy: string;
  period: string;
  value: string;
  pov: boolean;
  pvo: boolean;
  supplierIFA: string;
  sourceType?: string;
  commodity?: string;
};

const normalizeParseDataSupplier = (item: any): ParseDataSupplier | null => {
  const supplierIFA = String(item?.supplierIFA ?? item?.SupplierIFA ?? '').trim();
  const supplierName = String(item?.supplierName ?? item?.SupplierName ?? '').trim();
  if (!supplierIFA || !supplierName) return null;
  return { supplierIFA, supplierName };
};

const normalizeParseDataFinancialDetail = (item: any): ParseDataFinancialDetail | null => {
  const fy = String(item?.fy ?? item?.Fy ?? '').trim();
  const period = String(item?.period ?? item?.Period ?? '').trim();
  const value = String(item?.value ?? item?.Value ?? '').trim();
  const supplierIFA = String(item?.supplierIFA ?? item?.SupplierIFA ?? '').trim();
  if (!fy && !value) return null;
  return {
    fy,
    period,
    value,
    pov: Boolean(item?.pov ?? item?.Pov),
    pvo: Boolean(item?.pvo ?? item?.Pvo),
    supplierIFA,
    sourceType: String(item?.sourceType ?? item?.SourceType ?? ''),
    commodity: String(item?.commodity ?? item?.Commodity ?? '').trim(),
  };
};

export const PARSE_DATA_MIN_SEARCH_LENGTH = 2;

export const fetchParseDataSuppliers = async (search?: string, limit = 50) => {
  try {
    const trimmed = (search ?? '').trim();
    if (trimmed.length < PARSE_DATA_MIN_SEARCH_LENGTH) {
      return [];
    }
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    params.set('search', trimmed);
    const response = await getAxiosInstance().get(`${BASE_URL}/FetchParseDataSuppliers?${params.toString()}`);
    const rows = Array.isArray(response.data) ? response.data : [];
    return rows
      .map((item: any) => normalizeParseDataSupplier(item))
      .filter((item: ParseDataSupplier | null): item is ParseDataSupplier => Boolean(item));
  } catch (error) {
    console.error('Error fetching Parse Data suppliers:', error);
    return [];
  }
};

export const fetchParseDataSuppliersByIfas = async (ifaPos: string[]): Promise<ParseDataSupplier[]> => {
  try {
    const ifas = (Array.isArray(ifaPos) ? ifaPos : [])
      .map((ifa) => String(ifa ?? '').trim())
      .filter((ifa) => ifa.length > 0);
    if (ifas.length === 0) return [];

    const params = new URLSearchParams();
    ifas.forEach((ifa) => params.append('ifaPos', ifa));

    const response = await getAxiosInstance().get(
      `${BASE_URL}/FetchParseDataSuppliersByIfas?${params.toString()}`
    );
    const rows = Array.isArray(response.data) ? response.data : [];
    return rows
      .map((item: any) => normalizeParseDataSupplier(item))
      .filter((item: ParseDataSupplier | null): item is ParseDataSupplier => Boolean(item));
  } catch (error) {
    console.error('Error fetching Parse Data suppliers by IFA:', error);
    return [];
  }
};

export const fetchParseDataFinancialDetails = async (ifaPos: string | string[]): Promise<ParseDataFinancialDetail[]> => {
  try {
    const ifas = (Array.isArray(ifaPos) ? ifaPos : [ifaPos])
      .map((ifa) => String(ifa ?? '').trim())
      .filter((ifa) => ifa.length > 0);
    if (ifas.length === 0) return [];

    const params = new URLSearchParams();
    ifas.forEach((ifa) => params.append('ifaPos', ifa));

    const response = await getAxiosInstance().get(
      `${BASE_URL}/FetchParseDataFinancialDetails?${params.toString()}`
    );
    const rows = Array.isArray(response.data) ? response.data : [];
    return rows
      .map((item: any) => normalizeParseDataFinancialDetail(item))
      .filter((item: ParseDataFinancialDetail | null): item is ParseDataFinancialDetail => Boolean(item));
  } catch (error) {
    console.error('Error fetching Parse Data financial details:', error);
    throw error;
  }
};

// Fetch Commodities from API
export type PreferenceStatus = {
  id: number;
  status: string;
};

export const fetchPreferenceStatuses = async (): Promise<PreferenceStatus[]> => {
  try {
    const response = await getAxiosInstance().get(`${BASE_URL}/GetAllPreferenceStatuses`);
    const rows = Array.isArray(response.data) ? response.data : [];
    return rows
      .map((item: any) => ({
        id: Number(item?.id ?? item?.Id ?? item?.ID ?? 0),
        status: String(item?.status ?? item?.Status ?? '').trim(),
      }))
      .filter((item: PreferenceStatus) => item.id > 0 && item.status.length > 0);
  } catch (error) {
    console.error('Error fetching preference statuses:', error);
    return [];
  }
};

export const fetchCommodities = async () => {
  try {
    const response = await getAxiosInstance().get(`${BASE_URL}/Commodity`); // Update endpoint if needed
    return response.data;
  } catch (error) {
    console.error('Error fetching Commodities:', error);
    return [];
  }
};

// Add new Commodity to API
export const addCommodity = async (commodity: string) => {
  try {
    const response = await getAxiosInstance().post(`${BASE_URL}/CreateCommodity`, { commodity }); // Update endpoint if needed
    return response.data;
  } catch (error) {
    console.error('Error adding Commodity:', error);
    return null;
  }
};



// Fetch Products from API
export const fetchProducts = async () => {
  try {
    const response = await getAxiosInstance().get(`${BASE_URL}/ProductPortfolio`); // Update endpoint if needed
    return response.data;
  } catch (error) {
    console.error('Error fetching Products:', error);
    return [];
  }
};

// Add new Product to API
export const addProduct = async (Portfolio: string) => {
  try {
    const response = await getAxiosInstance().post(`${BASE_URL}/CreateProductPortfolio`, { Portfolio }); // Update endpoint if needed
    return response.data;
  } catch (error) {
    console.error('Error adding Product:', error);
    return null;
  }
};
// ApiService.tsx
// Centralized HTTP request utility as a React component (if needed) or export functions for API calls
// Usage: import { getSupplier, createSupplier, ... } from './ApiService';

// Example: Get supplier by name
export const getSupplier = async (name: string) => {
  try {
    const response = await getAxiosInstance().get(`${BASE_URL}/suppliers`, { params: { name } });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteSupplier = async (supplierId: number) => {
  try {
    const response = await getAxiosInstance().delete(`${BASE_URL}/DeleteSupplier`, { params: { supplierId } });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Example: Create new supplier
export const createSupplier = async (supplierData: any) => {
  try {
    const response = await getAxiosInstance().post(`${BASE_URL}/suppliers`, supplierData);
    return response.data;
  } catch (error) {
    throw error;
  }

};

// Example: Update supplier
export const updateSupplier = async (id: string, supplierData: any) => {
  try {
    const response = await getAxiosInstance().put(`${BASE_URL}/suppliers/${id}`, supplierData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Add more API methods as needed for commodities, products, etc.

// Fetch regions from API
export const fetchRegions = async () => {
  try {
    const response = await getAxiosInstance().get(`${BASE_URL}/GetAllEADRegions`); // Update endpoint if needed
    return response.data;
  } catch (error) {
    console.error('Error fetching regions:', error);
    return [];
  }
};

// Fetch F-Terms from API
export const fetchFTerms = async () => {
  try {
    const response = await getAxiosInstance().get(`${BASE_URL}/GetAllFTerms`); // Update endpoint if needed
    return response.data;
  } catch (error) {
    console.error('Error fetching F-Terms:', error);
    return [];
  }
};

// Fetch EAD Supplier Strategic Status from API
export const fetchSupplierStatuses = async () => {
  try {
    const response = await getAxiosInstance().get(`${BASE_URL}/GetAllEADSupplierStrategicStatuses`); // Update endpoint if needed
    return response.data;
  } catch (error) {
    console.error('Error fetching Supplier Statuses:', error);
    return [];
  }
};

// Fetch EAD Integrated Offerings from API
export const fetchIntegratedOfferings = async () => {
  try {
    const response = await getAxiosInstance().get(`${BASE_URL}/GetAllIntegratedOfferings`); // Update endpoint if needed
    return response.data;
  } catch (error) {
    console.error('Error fetching Integrated Offerings:', error);
    return [];
  }
};

// Fetch Production Locations from API
export const fetchProductionLocations = async () => {
  try {
    const response = await getAxiosInstance().get(`${BASE_URL}/GetAllCountries`); // Update endpoint if needed
    return response.data;
  } catch (error) {
    console.error('Error fetching Production Locations:', error);
    return [];
  }
};

export const getExistingSupplier = async (supplierID: any) => {
  const response = await getAxiosInstance().get(`${BASE_URL}/GetSupplierProfile/${supplierID}`, {
    timeout: 120000,
  });
  return response.data;
};
